// deps
import React, { memo, useMemo } from "react";
import { AriaHidden } from "@swiggy-private/core-ui";

//constants
import { ACCESSIBILITY_ROLES, TEXTS } from "@payment-ui/constants/index";

// interfaces
import { PaymentMethodNameProps } from "@payment-ui/widgets/payment-method-name/PaymentMethodName";

//helpers
import { isEmpty, replaceText } from "@swiggy-private/core-ui";

//components
import { CardNumberSubname } from "./components/card-number-subname";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// styles
import {
    Container,
    Name,
    NameWrapper,
    TruncateText,
    PromoMsg,
    SuperScriptText,
    DiscountCallout,
    DiscountList,
    DiscountBulletItem,
    Badge,
    RecommendedTag,
} from "./styles/PaymentMethodName.styled";

function PaymentMethodName(props: PaymentMethodNameProps): React.ReactElement {
    const colorToken = props.color || "text_High_Emphasis";

    const renderDiscounts = useMemo(() => {
        if (!props.enabled) {
            return null;
        }

        if (!isEmpty(props.discounts)) {
            return (
                <DiscountList>
                    {props.discounts.map((discount, index) => (
                        <DiscountBulletItem key={index}>
                            <Text variant="V2.body3.neutral.regular" color="positive">
                                {discount}
                            </Text>
                        </DiscountBulletItem>
                    ))}
                </DiscountList>
            );
        }

        if (!isEmpty(props.discount)) {
            return (
                <DiscountCallout $isDisabled={!props.enabled}>
                    <Text variant="V2.body3.neutral.regular" color="positive">
                        {props.discount}
                    </Text>
                </DiscountCallout>
            );
        }

        return null;
    }, [props.enabled, props.discounts, props.discount]);

    return (
        <Container $isDisabled={!props.enabled} data-testid={props["data-testid"]}>
            <Name className={props.className}>
                {props?.recommended && (
                    <RecommendedTag variant="V2.overline.neutral.bold" color="text_Button_Secondary">
                        {props.recommended}
                    </RecommendedTag>
                )}
                {props.superScriptText && (
                    <SuperScriptText $isDisabled={!props.enabled}>
                        <Text color="text_Med_Emphasis" variant="V2.body2.neutral.regular">
                            {props.superScriptText}
                        </Text>
                    </SuperScriptText>
                )}
                <NameWrapper
                    aria-label={props.ariaLabel}
                    aria-disabled={!props.enabled}
                    tabIndex={0}
                    role={props.isTurboPaymentMethod ? ACCESSIBILITY_ROLES.GENERIC : ACCESSIBILITY_ROLES.BUTTON}
                    $isDisabled={!props.enabled}
                >
                    <TruncateText>
                        <Text variant="V2.body1.neutral.medium" color={colorToken} maxLines={1}>
                            {props.displayName}
                        </Text>
                    </TruncateText>
                    {props.subName && (
                        <CardNumberSubname
                            cardNumber={props.subName}
                            isTurboPaymentMethod={!!props.isTurboPaymentMethod}
                        />
                    )}
                    {props?.isNewBadgeEnabled && (
                        <Badge>
                            <Text variant="V2.caption.neutral.bold" color="background_Primary">
                                {TEXTS.NEW}
                            </Text>
                        </Badge>
                    )}
                </NameWrapper>
                {props.promoMsg && props.promoMsg.length > 0 && (
                    <AriaHidden as="div" data-testid="promo-msg">
                        <PromoMsg className={props.promoMsgClass} $isDisabled={!props.enabled}>
                            <Text variant="V2.body3.neutral.regular" color="text_Med_Emphasis">
                                {replaceText(props.promoMsg)}
                                {props.dynamicPromoMsg}
                            </Text>
                        </PromoMsg>
                    </AriaHidden>
                )}
                {props.children}
                {renderDiscounts}
            </Name>
        </Container>
    );
}

export default memo<PaymentMethodNameProps>(PaymentMethodName);
